import React, { useEffect, useState } from "react";
import { Sidebar } from "primereact/sidebar";
import { Card } from "react-bootstrap";
import { Button } from "primereact/button";
import { RadioButton } from "primereact/radiobutton";
import { GiTwoCoins } from "react-icons/gi";
import api from "../api/axios";
import { toast, Bounce } from "react-toastify";
import { Badge } from "primereact/badge";
import { loadRazorpayScript } from "../utils/loadRazorpayScript";

const emitToast = (message, type = "default", autoClose = 5000) => {
  toast(message, {
    position: "bottom-left",
    autoClose,
    hideProgressBar: false,
    closeOnClick: false,
    pauseOnHover: true,
    draggable: true,
    theme: "dark",
    transition: Bounce,
    type,
  });
};

const CoinCheckoutSidebar = ({
  visible,
  onHide,
  pack,
  isAuthenticated,
}) => {
  const [addresses, setAddresses] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visible || !isAuthenticated) return;

    const fetchProfile = async () => {
      try {
        const res = await api.get(
          `/customerProfileRoutes/customer_profile_data`
        );

        if (res.data.success) {
          setCustomer(res.data.data);
          const list = res.data.data.details || [];
          setAddresses(list);

          const defaultAddr = list.find((a) => a.is_default);
          setSelectedAddressId(
            defaultAddr?.customer_details_id || list[0]?.customer_details_id
          );
        }
      } catch (error) {
        console.error("Profile fetch error:", error);
        emitToast("Failed to load billing profile", "error", 3000);
      }
    };

    fetchProfile();
  }, [visible, isAuthenticated]);

  if (!pack) return null;

  const subtotal = Number(pack.price || 0);
  const total = Number(subtotal.toFixed(2));

  const startPayment = async () => {
    if (loading) return;

    try {
      const address = addresses.find(
        (a) => a.customer_details_id === selectedAddressId
      );

      if (!address || !customer) {
        emitToast("Missing billing information ❗", "warning", 4000);
        return;
      }

      setLoading(true);

      const razorpayLoaded = await loadRazorpayScript();

      if (!razorpayLoaded) {
        emitToast("Failed to load payment gateway", "error", 3000);
        return;
      }

      const payload = {
        coin_pack_id: pack.id,
        phone: address.phone_no || customer.phone_no,
        email: customer.C_email,
        address1: address.address1,
        address2: address.address2 || "",
        city: address.city,
        state: address.state,
        country: address.country || "India",
        zipcode: address.zipcode,
        gst_no: address.gst_no || null,
      };

      const { data } = await api.post(
        `/pcnpcks/customer/coin/create-order`,
        payload
      );

      if (!data.success) {
        emitToast("Order creation failed ❌", "error", 5000);
        return;
      }

      const { order, key } = data;

      const rzp = new window.Razorpay({
        key,
        order_id: order.id,
        amount: order.amount,
        currency: order.currency,
        name: "Shaadi Material",
        description: "Coin Pack Purchase",

        handler: async (response) => {
          try {
            await api.post(`/pcnpcks/customer/coin/verify-payment`, response);
            emitToast("Payment paid successfully 🪙", "success", 6000);
            onHide();
          } catch (error) {
            console.error("Verify payment error:", error);
            emitToast(
              error?.response?.data?.message || "Payment verification failed",
              "error",
              4000
            );
          } finally {
            setLoading(false);
          }
        },

        prefill: {
          name: `${customer?.C_fname || ""} ${customer?.C_lname || ""}`.trim(),
          email: customer?.C_email || "",
          contact: address.phone_no || customer?.phone_no || "",
        },

        theme: {
          color: "#c59d5f",
        },

        modal: {
          ondismiss: () => {
            emitToast("Payment cancelled ❌", "info", 4000);
            setLoading(false);
          },
        },
      });

      rzp.open();
    } catch (err) {
      console.error("Buy now error:", err);

      if (err.response?.data?.code === "NO_ACTIVE_SUBSCRIPTION") {
        emitToast("Please buy a subscription first 🔒", "warning", 6000);
        return;
      }

      emitToast(
        err.response?.data?.message || "Something went wrong 🚨",
        "error",
        6000
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sidebar
      visible={visible}
      position="right"
      onHide={onHide}
      style={{ width: "420px" }}
      className="shortCartSide"
    >
      <h4 className="mb-4 text-light">Checkout</h4>

      <Card className="mb-3 coincheckout position-relative overflow-hidden">
        <div className="offerBadge">{pack.discountPercentage}% OFF</div>
        <Card.Body>
          <h4 className="text-pink">
            {pack.name}{" "}
            <small className="ms-3">
              <Badge value="Credits" severity="success"></Badge>
            </small>
          </h4>

          <p className="mb-1 cvalue">
            <GiTwoCoins /> {pack.credits}
          </p>

          {pack.hasDiscount && pack.originalPrice ? (
            <>
              <span className="text-secondary text-decoration-line-through">
                {pack.originalPrice.toLocaleString()} INR
              </span>
              <h5>{pack.price.toLocaleString()} INR</h5>
            </>
          ) : (
            <h4>{pack.price.toLocaleString()} INR</h4>
          )}
        </Card.Body>
      </Card>

      <hr />

      <h5 className="mb-4 text-pink d-block">Billing Address</h5>

      {addresses.length === 0 && (
        <p className="text-secondary">No address found</p>
      )}

      {addresses.map((addr) => (
        <div
          key={addr.customer_details_id}
          className="d-flex align-items-start gap-2 mb-2 "
        >
          <RadioButton
            value={addr.customer_details_id}
            checked={selectedAddressId === addr.customer_details_id}
            onChange={(e) => setSelectedAddressId(e.value)}
          />
          <span
            className="text-light"
            style={{ fontSize: "15px", letterSpacing: "0.5px", lineHeight: "26px" }}
          >
            {addr.address1}, {addr.city}, {addr.state}
          </span>
        </div>
      ))}

      <hr />

      <div className="d-flex justify-content-between mb-2 renewpart text-pink">
        <span className="">Original Price</span>
        <span className="text-secondary text-decoration-line-through">
          {pack.originalPrice.toLocaleString()} INR
        </span>
      </div>

      <div className="d-flex justify-content-between mb-2 renewpart text-pink">
        <span className="">Subtotal</span>
        <span className="fw-semibold">{subtotal} INR</span>
      </div>

      <hr />

      <div className="d-flex justify-content-between mb-4 renewpart">
        <h3 className="fw-bold mb-0" style={{color:'#ff71bc'}}>Total</h3>
        <h3 className="fw-bold mb-0" style={{color:'#ff71bc'}}>{total} INR</h3>
      </div>

      <Button
        label={loading ? "Processing..." : `Pay ${total} INR`}
        className="w-100 mt-3 custbuttonpink"
        loading={loading}
        disabled={!selectedAddressId || loading}
        onClick={startPayment}
        style={{ fontSize: "22px" }}
      />
    </Sidebar>
  );
};

export default CoinCheckoutSidebar;